import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Globally tag every GET to translatable public endpoints with the user's
// current language so the backend can return CMS content (courses, FAQ,
// blog…) rendered in the right locale. Other endpoints are untouched.
const _TRANSLATABLE_PATH_RE = /\/api\/(courses|faq|blog)(\/|\?|$)/;
axios.interceptors.request.use((config) => {
  try {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get') return config;
    const url = config.url || '';
    if (!_TRANSLATABLE_PATH_RE.test(url)) return config;
    const lang = (typeof window !== 'undefined' && window.localStorage.getItem('i18nextLng')) || 'fr';
    if (!lang || lang === 'fr') return config;
    config.params = { ...(config.params || {}), lang };
  } catch {
    // best-effort: never break a request because of i18n tagging
  }
  return config;
});

export const api = {
  // Courses
  getCourses: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await axios.get(`${API_URL}/courses${params ? `?${params}` : ''}`);
    return response.data;
  },
  
  getCourse: async (id) => {
    const response = await axios.get(`${API_URL}/courses/${id}`);
    return response.data;
  },
  
  createCourse: async (data) => {
    const response = await axios.post(`${API_URL}/admin/courses`, data);
    return response.data;
  },
  
  updateCourse: async (id, data) => {
    const response = await axios.put(`${API_URL}/admin/courses/${id}`, data);
    return response.data;
  },
  
  publishCourse: async (id) => {
    const response = await axios.put(`${API_URL}/admin/courses/${id}/publish`);
    return response.data;
  },
  
  deleteCourse: async (id) => {
    const response = await axios.delete(`${API_URL}/admin/courses/${id}`);
    return response.data;
  },
  
  // Episodes
  addEpisode: async (courseId, episode) => {
    const response = await axios.post(`${API_URL}/admin/courses/${courseId}/episodes`, episode);
    return response.data;
  },
  
  deleteEpisode: async (courseId, episodeId) => {
    const response = await axios.delete(`${API_URL}/admin/courses/${courseId}/episodes/${episodeId}`);
    return response.data;
  },
  
  // Sessions
  getSessions: async (courseId = null) => {
    const params = courseId ? `?course_id=${courseId}` : '';
    const response = await axios.get(`${API_URL}/sessions${params}`);
    return response.data;
  },
  
  getSession: async (id) => {
    const response = await axios.get(`${API_URL}/sessions/${id}`);
    return response.data;
  },
  
  createSession: async (data) => {
    const response = await axios.post(`${API_URL}/admin/sessions`, data);
    return response.data;
  },
  
  deleteSession: async (id) => {
    const response = await axios.delete(`${API_URL}/admin/sessions/${id}`);
    return response.data;
  },
  
  // Enrollments
  getEnrollments: async () => {
    const response = await axios.get(`${API_URL}/enrollments`);
    return response.data;
  },
  
  createEnrollment: async (courseId) => {
    const response = await axios.post(`${API_URL}/enrollments`, { course_id: courseId });
    return response.data;
  },
  
  updateProgress: async (enrollmentId, episodeId, completed) => {
    const response = await axios.put(
      `${API_URL}/enrollments/${enrollmentId}/progress?episode_id=${episodeId}&completed=${completed}`
    );
    return response.data;
  },
  
  // Applications
  getMyApplications: async () => {
    const response = await axios.get(`${API_URL}/applications`);
    return response.data;
  },
  
  createApplication: async (data) => {
    const response = await axios.post(`${API_URL}/applications`, data);
    return response.data;
  },
  
  getAllApplications: async (status = null) => {
    const params = status ? `?status=${status}` : '';
    const response = await axios.get(`${API_URL}/admin/applications${params}`);
    return response.data;
  },
  
  updateApplicationStatus: async (id, status) => {
    const response = await axios.put(`${API_URL}/admin/applications/${id}/status?status=${status}`);
    return response.data;
  },
  
  // Users
  getAllUsers: async () => {
    const response = await axios.get(`${API_URL}/admin/users`);
    return response.data;
  },
  
  updateUserRole: async (userId, role) => {
    const response = await axios.put(`${API_URL}/admin/users/${userId}/role?role=${role}`);
    return response.data;
  },

  getUserRecap: async (userId) => {
    const response = await axios.get(`${API_URL}/admin/users/${userId}/recap`);
    return response.data;
  },

  // Trainers (formateurs)
  createTrainer: async (data) => {
    const response = await axios.post(`${API_URL}/admin/trainers`, data);
    return response.data;
  },

  updateTrainerAssignments: async (trainerId, courseIds) => {
    const response = await axios.put(`${API_URL}/admin/trainers/${trainerId}/assignments`, { course_ids: courseIds });
    return response.data;
  },
  
  // Stats
  getStats: async () => {
    const response = await axios.get(`${API_URL}/admin/stats`);
    return response.data;
  },

  getApplicationsInsights: async () => {
    const response = await axios.get(`${API_URL}/admin/applications/insights`);
    return response.data;
  },

  // FAQ
  listFaq: async (params = {}) => {
    const response = await axios.get(`${API_URL}/faq`, { params });
    return response.data;
  },

  createFaq: async (data) => {
    const response = await axios.post(`${API_URL}/faq`, data);
    return response.data;
  },

  updateFaq: async (id, data) => {
    const response = await axios.patch(`${API_URL}/faq/${id}`, data);
    return response.data;
  },

  deleteFaq: async (id) => {
    const response = await axios.delete(`${API_URL}/faq/${id}`);
    return response.data;
  },
  
  // Formateur
  getInstructorCourses: async () => {
    const response = await axios.get(`${API_URL}/formateur/courses`);
    return response.data;
  },
  
  getInstructorStudents: async () => {
    const response = await axios.get(`${API_URL}/formateur/students`);
    return response.data;
  },
  
  // Payments
  createCheckout: async (courseId, sessionId = null) => {
    const response = await axios.post(`${API_URL}/checkout/create`, {
      course_id: courseId,
      session_id: sessionId,
      origin_url: window.location.origin
    });
    return response.data;
  },
  
  getCheckoutStatus: async (sessionId) => {
    const response = await axios.get(`${API_URL}/checkout/status/${sessionId}`);
    return response.data;
  },

  // ============ Phase A — Espace élève enrichi ============
  // Knowledge base
  listKbArticles: async (params = {}) => {
    const response = await axios.get(`${API_URL}/kb/articles`, { params });
    return response.data;
  },
  getKbArticle: async (slug) => {
    const response = await axios.get(`${API_URL}/kb/articles/${slug}`);
    return response.data;
  },
  createKbArticle: async (data) => {
    const response = await axios.post(`${API_URL}/admin/kb/articles`, data);
    return response.data;
  },
  updateKbArticle: async (id, data) => {
    const response = await axios.patch(`${API_URL}/admin/kb/articles/${id}`, data);
    return response.data;
  },
  deleteKbArticle: async (id) => {
    const response = await axios.delete(`${API_URL}/admin/kb/articles/${id}`);
    return response.data;
  },
  // Course manual
  getCourseManual: async (courseId) => {
    const response = await axios.get(`${API_URL}/courses/${courseId}/manual`);
    return response.data;
  },
  upsertCourseManual: async (courseId, data) => {
    const response = await axios.put(`${API_URL}/admin/courses/${courseId}/manual`, data);
    return response.data;
  },
  // Points & referrals
  getMyPoints: async () => {
    const response = await axios.get(`${API_URL}/me/points`);
    return response.data;
  },
  getMyPointsHistory: async () => {
    const response = await axios.get(`${API_URL}/me/points/history`);
    return response.data;
  },
  getMyReferrals: async () => {
    const response = await axios.get(`${API_URL}/me/referrals`);
    return response.data;
  },
  resolveReferral: async (code) => {
    const response = await axios.get(`${API_URL}/referral/resolve/${code}`);
    return response.data;
  },

  // ─── Manuels & QCM ───
  // Admin
  uploadManual: async (file, title, description = '', course_id = null) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    fd.append('description', description);
    if (course_id) fd.append('course_id', course_id);
    const res = await axios.post(`${API_URL}/admin/manuals/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  updateManualMeta: async (id, payload) => {
    const res = await axios.patch(`${API_URL}/admin/manuals/${id}`, payload);
    return res.data;
  },
  updateManualChapters: async (id, chapters) => {
    const res = await axios.put(`${API_URL}/admin/manuals/${id}/chapters`, { chapters });
    return res.data;
  },
  reExtractChapters: async (id) => {
    const res = await axios.post(`${API_URL}/admin/manuals/${id}/re-extract`);
    return res.data;
  },
  deleteManual: async (id) => {
    const res = await axios.delete(`${API_URL}/admin/manuals/${id}`);
    return res.data;
  },
  // Shared
  listManuals: async (course_id = null) => {
    const url = course_id ? `${API_URL}/manuals?course_id=${course_id}` : `${API_URL}/manuals`;
    const res = await axios.get(url);
    return res.data;
  },
  getManual: async (id) => {
    const res = await axios.get(`${API_URL}/manuals/${id}`);
    return res.data;
  },
  getManualFileUrl: (id) => `${API_URL}/manuals/${id}/file`,
  // QCM
  createQcm: async (payload) => {
    const res = await axios.post(`${API_URL}/admin/qcms`, payload);
    return res.data;
  },
  generateQcmAI: async (payload) => {
    const res = await axios.post(`${API_URL}/admin/qcms/generate`, payload);
    return res.data;
  },
  updateQcm: async (id, payload) => {
    const res = await axios.patch(`${API_URL}/admin/qcms/${id}`, payload);
    return res.data;
  },
  deleteQcm: async (id) => {
    const res = await axios.delete(`${API_URL}/admin/qcms/${id}`);
    return res.data;
  },
  getQcm: async (id) => {
    const res = await axios.get(`${API_URL}/qcms/${id}`);
    return res.data;
  },
  listQcmsForManual: async (manual_id) => {
    const res = await axios.get(`${API_URL}/manuals/${manual_id}/qcms`);
    return res.data;
  },
  submitQcmAttempt: async (qcm_id, answers) => {
    const res = await axios.post(`${API_URL}/qcms/${qcm_id}/attempt`, { answers });
    return res.data;
  },
  getMyQcmAttempts: async (qcm_id) => {
    const res = await axios.get(`${API_URL}/qcms/${qcm_id}/my-attempts`);
    return res.data;
  },
  getMyManualProgress: async (manual_id) => {
    const res = await axios.get(`${API_URL}/student/manuals/${manual_id}/progress`);
    return res.data;
  },
  // Formateur / Admin
  getClassResults: async (manual_id) => {
    const res = await axios.get(`${API_URL}/formateur/manuals/${manual_id}/class-results`);
    return res.data;
  },
  getQcmResults: async (qcm_id) => {
    const res = await axios.get(`${API_URL}/formateur/qcm/${qcm_id}/results`);
    return res.data;
  },
  exportClassResultsCsv: (manual_id) => `${API_URL}/formateur/manuals/${manual_id}/export.csv`,

  // ─── Programme PDF + Marketing Leads ───
  uploadProgrammePdf: async (course_id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API_URL}/admin/courses/${course_id}/programme-pdf`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  deleteProgrammePdf: async (course_id) => {
    const res = await axios.delete(`${API_URL}/admin/courses/${course_id}/programme-pdf`);
    return res.data;
  },
  requestProgramme: async (course_id, payload) => {
    const res = await axios.post(`${API_URL}/courses/${course_id}/request-programme`, payload);
    return res.data;
  },
  getProgrammeDownloadUrl: (course_id, token) =>
    `${API_URL}/courses/${course_id}/programme-download?token=${encodeURIComponent(token)}`,
  listMarketingLeads: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const res = await axios.get(`${API_URL}/admin/marketing-leads${qs ? `?${qs}` : ''}`);
    return res.data;
  },
  getMarketingLeadsStats: async () => {
    const res = await axios.get(`${API_URL}/admin/marketing-leads/stats`);
    return res.data;
  },
  updateMarketingLead: async (id, payload) => {
    const res = await axios.patch(`${API_URL}/admin/marketing-leads/${id}`, payload);
    return res.data;
  },
  deleteMarketingLead: async (id) => {
    const res = await axios.delete(`${API_URL}/admin/marketing-leads/${id}`);
    return res.data;
  },
  exportMarketingLeadsUrl: () => `${API_URL}/admin/marketing-leads/export.csv`,

  // ─── SMTP / Notifications ───
  getSmtpSettings: async () => {
    const res = await axios.get(`${API_URL}/admin/smtp/settings`);
    return res.data;
  },
  saveSmtpSettings: async (payload) => {
    const res = await axios.put(`${API_URL}/admin/smtp/settings`, payload);
    return res.data;
  },
  testSmtp: async (to) => {
    const res = await axios.post(`${API_URL}/admin/smtp/test`, { to });
    return res.data;
  },
  listSmtpLogs: async (limit = 50) => {
    const res = await axios.get(`${API_URL}/admin/smtp/logs?limit=${limit}`);
    return res.data;
  },

  // ─── Blog (public + admin) ───
  listBlogPosts: async (params = {}) => {
    const res = await axios.get(`${API_URL}/blog/posts`, { params });
    return res.data;
  },
  getBlogPost: async (slug) => {
    const res = await axios.get(`${API_URL}/blog/posts/${slug}`);
    return res.data;
  },
  trackBlogVisit: async (slug, lang = 'fr', referrer = '', query = '') => {
    try {
      await axios.post(`${API_URL}/blog/track`, { slug, lang, referrer, query });
    } catch (_) { /* fire-and-forget */ }
  },
  // Admin blog
  adminListBlogPosts: async () => {
    const res = await axios.get(`${API_URL}/admin/blog/posts`);
    return res.data;
  },
  adminCreateBlogPost: async (payload) => {
    const res = await axios.post(`${API_URL}/admin/blog/posts`, payload);
    return res.data;
  },
  adminUpdateBlogPost: async (slug, payload) => {
    const res = await axios.patch(`${API_URL}/admin/blog/posts/${slug}`, payload);
    return res.data;
  },
  adminDeleteBlogPost: async (slug) => {
    const res = await axios.delete(`${API_URL}/admin/blog/posts/${slug}`);
    return res.data;
  },

  // ─── SEO tracking ───
  listSeoKeywords: async () => {
    const res = await axios.get(`${API_URL}/admin/seo/keywords`);
    return res.data;
  },
  createSeoKeyword: async (payload) => {
    const res = await axios.post(`${API_URL}/admin/seo/keywords`, payload);
    return res.data;
  },
  updateSeoKeyword: async (id, payload) => {
    const res = await axios.patch(`${API_URL}/admin/seo/keywords/${id}`, payload);
    return res.data;
  },
  deleteSeoKeyword: async (id) => {
    const res = await axios.delete(`${API_URL}/admin/seo/keywords/${id}`);
    return res.data;
  },
  checkSeoKeyword: async (id) => {
    const res = await axios.post(`${API_URL}/admin/seo/keywords/${id}/check`);
    return res.data;
  },
  checkAllSeoKeywords: async () => {
    const res = await axios.post(`${API_URL}/admin/seo/check-all`);
    return res.data;
  },
  getSeoStats: async () => {
    const res = await axios.get(`${API_URL}/admin/seo/stats`);
    return res.data;
  },
  // ─── Site-wide analytics ───
  trackSiteVisit: async (path, lang = 'fr', referrer = '') => {
    try {
      await axios.post(`${API_URL}/analytics/track`, { path, lang, referrer });
    } catch (_) { /* fire-and-forget */ }
  },
  getPublicVisitTotal: async () => {
    const res = await axios.get(`${API_URL}/analytics/total`);
    return res.data;
  },
  getAnalyticsStats: async (days = 30) => {
    const res = await axios.get(`${API_URL}/admin/analytics/stats?days=${days}`);
    return res.data;
  },
  generateSeoKeywords: async (topic, count = 12) => {
    const res = await axios.post(`${API_URL}/admin/seo/keywords/generate`, { topic, count });
    return res.data;
  },

  // ─── Dossier pipeline (workflow + magic link + archive) ───
  getDossierRequirements: async () => {
    const res = await axios.get(`${API_URL}/dossier/requirements`);
    return res.data;
  },
  setDossierRequirements: async (requirements) => {
    const res = await axios.put(`${API_URL}/admin/dossier/requirements`, { requirements });
    return res.data;
  },
  myDossier: async () => {
    const res = await axios.get(`${API_URL}/dossier/my`);
    return res.data;
  },
  uploadDossierDocument: async (appId, docKey, file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post(
      `${API_URL}/dossier/applications/${appId}/documents/${docKey}/upload`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data;
  },
  downloadDossierDocument: async (appId, docKey) => {
    const res = await axios.get(
      `${API_URL}/dossier/applications/${appId}/documents/${docKey}/download`,
      { responseType: 'blob' }
    );
    return res.data;
  },

  // ── User-level documents (CNI, justificatif, RIB) — bound to the candidate,
  // reused for every future application.
  getMyDocuments: async () => {
    const res = await axios.get(`${API_URL}/users/me/documents`);
    return res.data;
  },
  uploadMyDocument: async (docKey, file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post(
      `${API_URL}/users/me/documents/${docKey}/upload`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data;
  },
  downloadMyDocument: async (docKey) => {
    const res = await axios.get(
      `${API_URL}/users/me/documents/${docKey}/download`,
      { responseType: 'blob' }
    );
    return res.data;
  },
  deleteMyDocument: async (docKey) => {
    const res = await axios.delete(`${API_URL}/users/me/documents/${docKey}`);
    return res.data;
  },
  adminTransitionApplication: async (appId, status, note = '') => {
    const res = await axios.post(`${API_URL}/admin/dossier/applications/${appId}/transition`, { status, note });
    return res.data;
  },
  adminValidateDocument: async (appId, docKey, rejection_reason = null) => {
    const res = await axios.post(
      `${API_URL}/admin/dossier/applications/${appId}/documents/${docKey}/validate`,
      { rejection_reason }
    );
    return res.data;
  },
  adminMarkPaid: async (appId, payload) => {
    const res = await axios.post(`${API_URL}/admin/dossier/applications/${appId}/payment/mark-paid`, payload);
    return res.data;
  },
  adminResendMagicLink: async (appId) => {
    const res = await axios.post(`${API_URL}/admin/dossier/applications/${appId}/resend-magic-link`);
    return res.data;
  },
  // ─── Users (admin) ───
  listUsers: async (includeArchived = false) => {
    const res = await axios.get(`${API_URL}/admin/users?include_archived=${includeArchived}`);
    return res.data;
  },
  archiveUser: async (userId) => {
    const res = await axios.delete(`${API_URL}/admin/users/${userId}`);
    return res.data;
  },
  restoreUser: async (userId) => {
    const res = await axios.post(`${API_URL}/admin/users/${userId}/restore`);
    return res.data;
  },
  // ─── Magic link (public) ───
  verifyMagicLink: async (token) => {
    const res = await axios.get(`${API_URL}/auth/magic-link/${token}/verify`);
    return res.data;
  },
  setPasswordViaMagicLink: async (token, newPassword) => {
    const res = await axios.post(`${API_URL}/auth/magic-link/set-password`, { token, new_password: newPassword });
    return res.data;
  },

  // ─── SEO — City landing pages ───
  listCityPagesPublic: async () => {
    const res = await axios.get(`${API_URL}/seo/city-pages`);
    return res.data;
  },
  getCityPagePublic: async (slug) => {
    const res = await axios.get(`${API_URL}/seo/city-pages/${slug}`);
    return res.data;
  },
  adminListCityPages: async () => {
    const res = await axios.get(`${API_URL}/admin/seo/city-pages`);
    return res.data;
  },
  adminCreateCityPage: async (payload) => {
    const res = await axios.post(`${API_URL}/admin/seo/city-pages`, payload);
    return res.data;
  },
  adminUpdateCityPage: async (id, payload) => {
    const res = await axios.patch(`${API_URL}/admin/seo/city-pages/${id}`, payload);
    return res.data;
  },
  adminDeleteCityPage: async (id) => {
    const res = await axios.delete(`${API_URL}/admin/seo/city-pages/${id}`);
    return res.data;
  },
  adminSeedCityPages: async () => {
    const res = await axios.post(`${API_URL}/admin/seo/city-pages/seed`);
    return res.data;
  },
};
